/* eslint-disable @typescript-eslint/no-explicit-any */
import { SetMetadata } from '@nestjs/common';

/**
 * 绑定动作元数据键
 */
export const BIND_ACTION_METADATA_KEY = 'automation:action';

/**
 * 绑定动作元数据接口
 */
export interface BindActionMetadata {
  trigger: string;
  actionName?: string;
  actionDesc?: string;
}

/**
 * Trigger 注册表
 * 
 * 用于确保每个 trigger 只能绑定一个方法，实现 1:1 绑定约束
 */
const TRIGGER_REGISTRY = new Map<string, { className: string; methodName: string }>();

/**
 * 获取已注册的 triggers
 */
export const getRegisteredTriggers = () => TRIGGER_REGISTRY;

/**
 * 清空 trigger 注册表
 */
export const clearTriggerRegistry = () => TRIGGER_REGISTRY.clear();

/**
 * @BindTrigger 装饰器
 * 
 * 方法装饰器，将方法绑定到指定的 trigger
 * 
 * 重要特性：
 * - 1:1 绑定约束：每个 trigger 只能绑定一个方法
 * - 重复绑定会抛出错误
 * 
 * @param trigger - Trigger 唯一标识符
 * @param options - 可选配置
 * @param options.actionName - 任务名称
 * @param options.actionDesc - 任务描述
 * 
 * @example
 * ```typescript
 * @Automation()
 * @Injectable()
 * export class EmailAutomation {
 *   @BindTrigger('send-email', {
 *     actionName: 'SendEmail',
 *     actionDesc: '发送邮件通知'
 *   })
 *   async sendEmail(to: string, subject: string) {
 *     // 邮件发送逻辑
 *   }
 * }
 * ```
 */
export const BindTrigger = (
  trigger: string,
  options?: { actionName?: string; actionDesc?: string },
): MethodDecorator => {
  return (target: any, propertyKey?: string | symbol, descriptor?: PropertyDescriptor) => {
    const className = target.constructor.name;
    const methodName = String(propertyKey);

    // 检查 trigger 是否已被注册
    if (TRIGGER_REGISTRY.has(trigger)) {
      const existing = TRIGGER_REGISTRY.get(trigger)!;
      throw new Error(
        `\n❌ @BindTrigger binding violation detected!\n` +
          `\nTrigger '${trigger}' is already bound to another method:\n` +
          `  Existing: ${existing.className}.${existing.methodName}\n` +
          `  Attempted: ${className}.${methodName}\n` +
          `\n@BindTrigger enforces strict 1-to-1 binding. Each trigger can only be bound to one method.\n`,
      );
    }

    // 注册 trigger
    TRIGGER_REGISTRY.set(trigger, { className, methodName });

    const metadata: BindActionMetadata = {
      trigger,
      actionName: options?.actionName,
      actionDesc: options?.actionDesc,
    };

    return SetMetadata(BIND_ACTION_METADATA_KEY, metadata)(target, propertyKey!, descriptor!);
  };
};
